import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Trash2, Phone, Mail, Smartphone, Pencil, X, MapPin, FileText, Receipt, Search, Truck, KeyRound, UserPlus, BadgePercent, RotateCcw } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, Tooltip, CartesianGrid } from "recharts";
import { api } from "@/lib/api";

const STATUS = {
  neu: { label: "Neu", cls: "bg-white text-[#050505]" },
  in_bearbeitung: { label: "In Bearbeitung", cls: "bg-yellow-500/20 text-yellow-300 border border-yellow-500/30" },
  unterwegs: { label: "Auf dem Weg zu dir", cls: "bg-blue-500/20 text-blue-300 border border-blue-500/30" },
  abgeschlossen: { label: "Abgeschlossen", cls: "bg-green-500/20 text-green-300 border border-green-500/30" },
  storniert: { label: "Storniert", cls: "bg-red-500/20 text-red-300 border border-red-500/30" },
};

const PAYMENT_STATUS = {
  unbezahlt: { label: "Unbezahlt", cls: "bg-white/10 text-[#ccc] border border-white/15" },
  bezahlt: { label: "Bezahlt", cls: "bg-emerald-500/20 text-emerald-300 border border-emerald-500/30" },
  erstattet: { label: "Erstattet", cls: "bg-purple-500/20 text-purple-300 border border-purple-500/30" },
};

const inputCls = "w-full bg-[#050505] border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-[#666] focus:border-white/40 focus:outline-none transition-colors duration-300";

function EditModal({ order, models, serviceTypes, onClose, onSaved }) {
  const [f, setF] = useState({ ...order, service_keys: order.service_keys || [], display_type: order.display_type || "oled" });
  const [saving, setSaving] = useState(false);
  const s = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const toggleKey = (key) => setF((p) => ({
    ...p, service_keys: p.service_keys.includes(key) ? p.service_keys.filter((k) => k !== key) : [...p.service_keys, key],
  }));

  const save = async () => {
    if (!f.wants_display && f.service_keys.length === 0) { toast.error("Bitte mindestens eine Reparatur wählen."); return; }
    setSaving(true);
    try {
      const { data } = await api.put(`/orders/${order.id}`, {
        first_name: f.first_name, last_name: f.last_name, email: f.email, phone: f.phone,
        street: f.street, zip: f.zip, city: f.city, device: f.device,
        wants_display: f.wants_display, display_type: f.display_type, service_keys: f.service_keys,
        message: f.message, status: f.status, device_code: f.device_code,
      });
      onSaved(data);
      toast.success("Bestellung aktualisiert.");
      onClose();
    } catch (e) { toast.error("Speichern fehlgeschlagen."); }
    finally { setSaving(false); }
  };

  const pillCls = (on) => `text-sm px-4 py-2 rounded-full border transition-colors duration-300 ${on ? "bg-white text-[#050505] border-white" : "border-white/15 text-[#ccc] hover:text-white hover:border-white/40"}`;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <motion.div initial={{ opacity: 0, scale: 0.96, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} data-testid="edit-modal"
        className="relative glass rounded-3xl p-5 sm:p-8 w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-display text-2xl">Bestellung bearbeiten</h3>
          <button onClick={onClose} className="text-[#888] hover:text-white"><X size={20} /></button>
        </div>
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <input data-testid="edit-first-name" value={f.first_name} onChange={s("first_name")} placeholder="Vorname" className={inputCls} />
            <input value={f.last_name} onChange={s("last_name")} placeholder="Nachname" className={inputCls} />
          </div>
          <input data-testid="edit-email" value={f.email} onChange={s("email")} placeholder="E-Mail" className={inputCls} />
          <input value={f.phone} onChange={s("phone")} placeholder="Telefon" className={inputCls} />
          <input value={f.street} onChange={s("street")} placeholder="Straße & Nr." className={inputCls} />
          <div className="grid grid-cols-3 gap-3 sm:gap-4">
            <input value={f.zip} onChange={s("zip")} placeholder="PLZ" className={inputCls} />
            <input value={f.city} onChange={s("city")} placeholder="Ort" className={`${inputCls} col-span-2`} />
          </div>
          <select data-testid="edit-device" value={f.device} onChange={s("device")} className={inputCls}>
            {models.map((m) => <option key={m} className="bg-[#0a0a0a]">{m}</option>)}
          </select>

          <div>
            <label className="block text-xs font-mono uppercase tracking-widest text-[#888] mb-2">Reparatur (Mehrfachauswahl)</label>
            <div className="flex flex-wrap gap-2">
              <button type="button" onClick={() => setF((p) => ({ ...p, wants_display: !p.wants_display }))} data-testid="edit-toggle-display" className={pillCls(f.wants_display)}>
                Display-Reparatur
              </button>
              {serviceTypes.map((svc) => (
                <button key={svc.key} type="button" onClick={() => toggleKey(svc.key)} data-testid={`edit-toggle-${svc.key}`} className={pillCls(f.service_keys.includes(svc.key))}>
                  {svc.name}
                </button>
              ))}
            </div>
            {f.wants_display && (
              <select value={f.display_type} onChange={s("display_type")} data-testid="edit-display-type" className={`${inputCls} mt-3`}>
                <option value="oled" className="bg-[#0a0a0a]">OLED</option>
                <option value="lcd" className="bg-[#0a0a0a]">LCD</option>
              </select>
            )}
          </div>

          <select data-testid="edit-status" value={f.status} onChange={s("status")} className={inputCls}>
            {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k} className="bg-[#0a0a0a]">{v.label}</option>)}
          </select>
          <input data-testid="edit-device-code" value={f.device_code || ""} onChange={s("device_code")} placeholder="Gerätecode (Entsperrcode)" className={inputCls} />
          <textarea rows={3} value={f.message} onChange={s("message")} placeholder="Nachricht" className={`${inputCls} resize-none`} />
        </div>
        <div className="flex gap-3 mt-6">
          <button onClick={save} disabled={saving} data-testid="edit-save" className="flex-1 rounded-full bg-white text-[#050505] py-3 font-medium hover:bg-[#d1d5db] transition-colors disabled:opacity-50">
            {saving ? "Speichern…" : "Speichern"}
          </button>
          <button onClick={onClose} className="px-6 rounded-full border border-white/15 text-[#ccc] hover:text-white transition-colors">Abbrechen</button>
        </div>
      </motion.div>
    </div>
  );
}

export default function OrdersView() {
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [monthly, setMonthly] = useState([]);
  const [range, setRange] = useState(12);
  const [models, setModels] = useState([]);
  const [serviceTypes, setServiceTypes] = useState([]);
  const [filter, setFilter] = useState("alle");
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    try {
      const [o, s, p, st] = await Promise.all([api.get("/orders"), api.get("/stats"), api.get("/pricing"), api.get("/service-types")]);
      setOrders(o.data);
      setStats(s.data);
      setModels(p.data.map((x) => x.model));
      setServiceTypes(st.data);
    } catch (e) { toast.error("Konnte Daten nicht laden."); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    api.get(`/stats/monthly?months=${range}`).then((r) => setMonthly(r.data)).catch(() => {});
  }, [range]);

  useEffect(() => { load(); }, [load]);

  const changeStatus = async (id, status) => {
    try {
      const { data } = await api.patch(`/orders/${id}/status`, { status });
      setOrders((p) => p.map((x) => (x.id === id ? data : x)));
      load();
      toast.success("Status aktualisiert – Kunde per E-Mail informiert.");
    } catch (e) { toast.error("Aktualisierung fehlgeschlagen."); }
  };

  const [invoicingId, setInvoicingId] = useState(null);
  const invoiceFromOrder = async (o) => {
    setInvoicingId(o.id);
    try {
      const { data } = await api.post(`/orders/${o.id}/invoice`);
      toast.success(o.email ? `Rechnung ${data.invoice_number} erstellt & an ${o.email} gesendet.` : `Rechnung ${data.invoice_number} erstellt.`);
    } catch (e) { toast.error(e.response?.data?.detail || "Rechnung fehlgeschlagen."); }
    finally { setInvoicingId(null); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/orders/${id}`);
      setOrders((p) => p.filter((x) => x.id !== id));
      load();
      toast.success("Bestellung gelöscht.");
    } catch (e) { toast.error("Löschen fehlgeschlagen."); }
  };

  const protocol = async (id) => {
    try {
      const res = await api.get(`/orders/${id}/protocol`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (e) { toast.error("Protokoll konnte nicht erstellt werden."); }
  };

  const [trackInput, setTrackInput] = useState({});
  const saveTracking = async (id) => {
    try {
      const { data } = await api.put(`/orders/${id}`, { tracking_number: (trackInput[id] ?? "").trim() });
      setOrders((p) => p.map((x) => (x.id === id ? data : x)));
      toast.success('Sendungsnummer gespeichert – Status auf "Auf dem Weg zu dir" gesetzt & Kunde per Mail informiert.');
    } catch (e) { toast.error("Speichern fehlgeschlagen."); }
  };

  const [creatingCustomerId, setCreatingCustomerId] = useState(null);
  const createCustomerFromOrder = async (o) => {
    setCreatingCustomerId(o.id);
    try {
      const { data } = await api.post(`/orders/${o.id}/create-customer`);
      setOrders((p) => p.map((x) => (x.id === o.id ? { ...x, customer_id: data.id, customer_number: data.customer_number } : x)));
      toast.success(`Kunde angelegt/verknüpft – Kundennummer ${data.customer_number}.`);
    } catch (e) { toast.error("Kunde konnte nicht angelegt werden."); }
    finally { setCreatingCustomerId(null); }
  };

  const [refundingId, setRefundingId] = useState(null);
  const refundOrder = async (o) => {
    if (!window.confirm(`Zahlung von ${o.total?.toFixed(2)} € für ${o.reference} wirklich über Stripe erstatten?`)) return;
    setRefundingId(o.id);
    try {
      const { data } = await api.post(`/orders/${o.id}/refund`);
      setOrders((p) => p.map((x) => (x.id === o.id ? data : x)));
      toast.success("Rückerstattung über Stripe ausgelöst – Kunde per Mail informiert.");
    } catch (e) { toast.error(e.response?.data?.detail || "Rückerstattung fehlgeschlagen."); }
    finally { setRefundingId(null); }
  };

  const q = query.trim().toLowerCase();
  const shown = orders.filter((r) => {
    if (filter !== "alle" && r.status !== filter) return false;
    if (!q) return true;
    const hay = `${r.first_name} ${r.last_name} ${r.reference || ""} ${r.device} ${r.email} ${r.phone}`.toLowerCase();
    return hay.includes(q);
  });
  const statCards = [
    { key: "total", label: "Bestellungen", val: stats?.total },
    { key: "neu", label: "Neu", val: stats?.neu },
    { key: "in_bearbeitung", label: "In Bearbeitung", val: stats?.in_bearbeitung },
    { key: "revenue", label: "Umsatz (€)", val: stats ? stats.revenue.toFixed(2) : undefined },
  ];

  return (
    <div data-testid="orders-view">
      <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl mb-6 sm:mb-8">Bestellungen</h1>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
        {statCards.map((c) => (
          <div key={c.key} data-testid={`stat-${c.key}`} className="rounded-2xl border border-white/10 bg-[#0a0a0a] p-4 sm:p-6">
            <p className="font-mono text-xs uppercase tracking-widest text-[#888]">{c.label}</p>
            <p className="font-display text-3xl sm:text-4xl lg:text-5xl mt-2 sm:mt-3">{c.val ?? "—"}</p>
          </div>
        ))}
      </div>

      {/* Monthly revenue chart */}
      <div data-testid="revenue-chart" className="rounded-2xl border border-white/10 bg-[#0a0a0a] p-6 mb-10">
        <div className="flex items-center justify-between mb-4">
          <p className="font-mono text-xs uppercase tracking-widest text-[#888]">Umsatz · letzte {range} Monate</p>
          <div className="flex items-center gap-1 bg-[#050505] border border-white/10 rounded-full p-1">
            {[6, 12].map((n) => (
              <button key={n} onClick={() => setRange(n)} data-testid={`range-${n}`}
                className={`text-xs px-3 py-1.5 rounded-full transition-colors ${range === n ? "bg-white text-[#050505]" : "text-[#aaa] hover:text-white"}`}>
                {n} Monate
              </button>
            ))}
          </div>
        </div>
        <div style={{ width: "100%", height: 240 }}>
          <ResponsiveContainer>
            <BarChart data={monthly} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" vertical={false} />
              <XAxis dataKey="label" tick={{ fill: "#888", fontSize: 11 }} axisLine={{ stroke: "rgba(255,255,255,0.1)" }} tickLine={false} />
              <Tooltip
                cursor={{ fill: "rgba(255,255,255,0.04)" }}
                contentStyle={{ background: "#050505", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, color: "#fff" }}
                formatter={(v) => [`${Number(v).toFixed(2)} €`, "Umsatz"]}
              />
              <Bar dataKey="revenue" fill="#ffffff" radius={[6, 6, 0, 0]} maxBarSize={44} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="mb-4">
        <div className="relative max-w-md">
          <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#666]" />
          <input
            data-testid="order-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Suche nach Name, Referenz oder Gerät…"
            className="w-full bg-[#050505] border border-white/10 rounded-full pl-11 pr-4 py-3 text-sm text-white placeholder:text-[#666] focus:border-white/40 focus:outline-none transition-colors"
          />
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-6">
        {["alle", "neu", "in_bearbeitung", "unterwegs", "abgeschlossen", "storniert"].map((f) => (
          <button key={f} onClick={() => setFilter(f)} data-testid={`filter-${f}`}
            className={`text-sm px-4 py-2 rounded-full border transition-colors duration-300 ${
              filter === f ? "bg-white text-[#050505] border-white" : "border-white/15 text-[#aaa] hover:text-white hover:border-white/40"
            }`}>
            {f === "alle" ? "Alle" : STATUS[f]?.label}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="text-[#888] font-mono text-sm py-20 text-center">Lädt…</p>
      ) : shown.length === 0 ? (
        <div data-testid="empty-state" className="rounded-2xl border border-white/10 bg-[#0a0a0a] p-16 text-center">
          <p className="text-[#888]">Keine Bestellungen in dieser Ansicht.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="orders-list">
          {shown.map((r) => (
            <div key={r.id} data-testid={`order-${r.id}`} className="rounded-2xl border border-white/10 bg-[#0a0a0a] p-4 sm:p-6 grid grid-cols-1 lg:grid-cols-12 gap-4 items-start">
              <div className="lg:col-span-4">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${STATUS[r.status]?.cls}`}>{STATUS[r.status]?.label}</span>
                  <span data-testid={`payment-status-${r.id}`} className={`text-xs px-2.5 py-1 rounded-full font-medium ${PAYMENT_STATUS[r.payment_status]?.cls || PAYMENT_STATUS.unbezahlt.cls}`}>
                    {PAYMENT_STATUS[r.payment_status]?.label || "Unbezahlt"}
                  </span>
                  {r.reference && <span className="font-mono text-xs text-white/90 bg-white/10 px-2 py-1 rounded-full" data-testid={`ref-${r.id}`}>{r.reference}</span>}
                  {r.customer_number && (
                    <span data-testid={`customer-badge-${r.id}`} className="text-xs px-2.5 py-1 rounded-full font-medium bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 inline-flex items-center gap-1">
                      Kunde: {r.customer_number}
                      {r.discount_percent > 0 && <><BadgePercent size={12} /> {r.discount_percent}%</>}
                    </span>
                  )}
                  {r.discount_codes?.length > 0 && (
                    <span data-testid={`coupon-badge-${r.id}`} className="text-xs px-2.5 py-1 rounded-full font-medium bg-amber-500/20 text-amber-300 border border-amber-500/30 inline-flex items-center gap-1">
                      <BadgePercent size={12} /> Code: {r.discount_codes.join(" + ")}
                      {r.coupon_discount_amount > 0 && <> (−{r.coupon_discount_amount.toFixed(2)} €)</>}
                      {r.free_shipping && <> (Freiversand)</>}
                    </span>
                  )}
                  {r.from_inquiry_id && (
                    <span data-testid={`from-inquiry-badge-${r.id}`} className="text-xs px-2.5 py-1 rounded-full font-medium bg-sky-500/20 text-sky-300 border border-sky-500/30 inline-flex items-center gap-1">
                      <FileText size={12} /> Aus Anfrage {r.inquiry_reference}
                    </span>
                  )}
                  {r.is_protection_repair && (
                    <span data-testid={`protection-badge-${r.id}`} className="text-xs px-2.5 py-1 rounded-full font-medium bg-violet-500/20 text-violet-300 border border-violet-500/30 inline-flex items-center gap-1">
                      Schutzpaket
                    </span>
                  )}
                  <span className="font-mono text-xs text-[#666]">
                    {new Date(r.created_at).toLocaleDateString("de-DE", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                  </span>
                </div>
                <p className="text-lg mt-2">{r.first_name} {r.last_name}</p>
                <div className="flex flex-col gap-1 mt-2 text-sm text-[#999]">
                  <a href={`mailto:${r.email}`} className="inline-flex items-center gap-2 hover:text-white transition-colors"><Mail size={14} />{r.email}</a>
                  <a href={`tel:${r.phone}`} className="inline-flex items-center gap-2 hover:text-white transition-colors"><Phone size={14} />{r.phone}</a>
                  <span className="inline-flex items-start gap-2"><MapPin size={14} className="mt-0.5" />{r.street}, {r.zip} {r.city}</span>
                </div>
              </div>
              <div className="lg:col-span-4">
                <div className="inline-flex items-center gap-2 text-sm text-white"><Smartphone size={14} className="text-[#888]" />{r.device}</div>
                <p className="text-sm text-[#ccc] mt-1">{r.service_label}</p>
                {r.addons?.length > 0 && (
                  <div data-testid={`addons-${r.id}`} className="flex flex-wrap gap-1.5 mt-1.5">
                    {r.addons.map((a) => (
                      <span key={a.key} className="text-xs px-2 py-0.5 rounded-full font-medium bg-sky-500/20 text-sky-300 border border-sky-500/30">
                        + {a.label} ({a.price.toFixed(2)} €)
                      </span>
                    ))}
                  </div>
                )}
                <p className="font-mono text-sm text-[#999] mt-1">{r.base_price?.toFixed(2)} € {r.addons_total > 0 ? `+ ${r.addons_total.toFixed(2)} € Extras ` : ""}+ {r.shipping?.toFixed(2)} € Versand = <span className="text-white">{r.total?.toFixed(2)} €</span></p>
                {r.message && <p className="text-sm text-[#888] mt-2 leading-relaxed">{r.message}</p>}
                {r.device_code && (
                  <p data-testid={`device-code-${r.id}`} className="inline-flex items-center gap-1.5 text-sm text-amber-300 mt-2 font-mono">
                    <KeyRound size={13} /> Gerätecode: {r.device_code}
                  </p>
                )}
              </div>
              <div className="lg:col-span-4 flex flex-col items-start lg:items-end gap-3 w-full">
                <div className="flex items-center flex-wrap gap-2 sm:gap-3">
                  <button onClick={() => invoiceFromOrder(r)} disabled={invoicingId === r.id} data-testid={`invoice-order-${r.id}`} title="Rechnung erstellen & per Mail senden" className="inline-flex items-center gap-1.5 text-xs rounded-lg border border-white/15 px-3 h-9 text-[#ccc] hover:text-white hover:border-white/40 transition-colors disabled:opacity-40">
                    <Receipt size={14} /> {invoicingId === r.id ? "…" : "Rechnung"}
                  </button>
                  {!r.customer_id && (
                    <button onClick={() => createCustomerFromOrder(r)} disabled={creatingCustomerId === r.id} data-testid={`create-customer-${r.id}`} title="Als Kunde anlegen" className="inline-flex items-center gap-1.5 text-xs rounded-lg border border-white/15 px-3 h-9 text-[#ccc] hover:text-white hover:border-white/40 transition-colors disabled:opacity-40">
                      <UserPlus size={14} /> {creatingCustomerId === r.id ? "…" : "Als Kunde anlegen"}
                    </button>
                  )}
                  <button onClick={() => protocol(r.id)} data-testid={`protocol-${r.id}`} title="Reparaturprotokoll (PDF)" className="w-9 h-9 rounded-lg border border-white/10 flex items-center justify-center text-[#888] hover:text-white hover:border-white/40 transition-colors"><FileText size={16} /></button>
                  {r.payment_status === "bezahlt" && (
                    <button onClick={() => refundOrder(r)} disabled={refundingId === r.id} data-testid={`refund-order-${r.id}`} title="Zahlung über Stripe erstatten" className="w-9 h-9 rounded-lg border border-white/10 flex items-center justify-center text-[#888] hover:text-purple-300 hover:border-purple-400/40 transition-colors disabled:opacity-40"><RotateCcw size={16} /></button>
                  )}
                  <button onClick={() => setEditing(r)} data-testid={`edit-${r.id}`} className="w-9 h-9 rounded-lg border border-white/10 flex items-center justify-center text-[#888] hover:text-white hover:border-white/40 transition-colors"><Pencil size={16} /></button>
                  <button onClick={() => remove(r.id)} data-testid={`delete-${r.id}`} className="w-9 h-9 rounded-lg border border-white/10 flex items-center justify-center text-[#888] hover:text-red-400 hover:border-red-400/40 transition-colors"><Trash2 size={16} /></button>
                </div>
                <select value={r.status} onChange={(e) => changeStatus(r.id, e.target.value)} data-testid={`status-select-${r.id}`}
                  className="bg-[#050505] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-white/40 focus:outline-none">
                  {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k} className="bg-[#0a0a0a]">{v.label}</option>)}
                </select>
                <div className="w-full flex gap-2 items-center">
                  <Truck size={14} className="text-[#888] shrink-0" />
                  <input
                    data-testid={`tracking-input-${r.id}`}
                    value={trackInput[r.id] ?? r.tracking_number ?? ""}
                    onChange={(e) => setTrackInput((p) => ({ ...p, [r.id]: e.target.value }))}
                    placeholder="Sendungsnummer (DHL)"
                    className="flex-1 bg-[#050505] border border-white/10 rounded-lg px-3 py-2 text-xs text-white placeholder:text-[#666] focus:border-white/40 focus:outline-none"
                  />
                  <button onClick={() => saveTracking(r.id)} data-testid={`save-tracking-${r.id}`}
                    className="shrink-0 text-xs rounded-lg border border-white/15 px-3 py-2 text-[#ccc] hover:text-white hover:border-white/40 transition-colors">
                    Speichern
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <AnimatePresence>
        {editing && <EditModal order={editing} models={models} serviceTypes={serviceTypes} onClose={() => setEditing(null)} onSaved={(d) => setOrders((p) => p.map((x) => (x.id === d.id ? d : x)))} />}
      </AnimatePresence>
    </div>
  );
}
